import React, { useEffect, useRef, useState } from "react";
import InfoIcon from "../InfoIcon";
import { typography, surfaces, borders, textColors, interactive } from "../../../design-system/tokens";

export const ChoiceField = ({ value, onChange, choices = [], label, help, infoText }) => {
  const useSegmented = Array.isArray(choices) && choices.length > 0 && choices.length <= 4;
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updateWidth = (next) => {
      const rounded = Number.isFinite(next) ? Math.round(next) : 0;
      setContainerWidth((prev) => (prev === rounded ? prev : rounded));
    };

    try {
      updateWidth(el.getBoundingClientRect().width);
    } catch (_) {}

    if (typeof ResizeObserver === "undefined") return;

    const obs = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect) updateWidth(rect.width);
    });
    obs.observe(el);
    return () => {
      try { obs.disconnect(); } catch (_) {}
    };
  }, []);

  const isCompact = containerWidth > 0 && containerWidth < 520;
  const compactCols = containerWidth < 360 ? 1 : 2;

  if (!useSegmented) {
    return (
      <div ref={containerRef} className={`edit-field-row ${isCompact ? "flex-col items-stretch space-x-0" : ""}`}>
        {label && (
          <span className={isCompact ? `block mb-1 ${typography.label} ${textColors.secondary}` : `${typography.label} ${textColors.secondary}`}>
            {label}
            {infoText ? <span className="ml-2"><InfoIcon text={infoText} /></span> : null}
          </span>
        )}
        <select
          className={`w-full px-4 py-2.5 rounded-xl border ${borders.default} ${surfaces.primary} ${typography.body} ${textColors.primary} focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-all duration-200 appearance-none bg-no-repeat bg-[length:16px_16px] bg-[right_12px_center]`}
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
            paddingRight: "2.5rem",
          }}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          {choices.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {help && (
          <div className={`mt-1 ${typography.caption} ${textColors.tertiary}`}>{help}</div>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`edit-field-row ${isCompact ? "flex-col items-stretch space-x-0" : ""}`}>
      {label && (
        <span className={isCompact ? `block mb-1 ${typography.label} ${textColors.secondary}` : `${typography.label} ${textColors.secondary}`}>
          {label}
          {infoText ? <span className="ml-2"><InfoIcon text={infoText} /></span> : null}
        </span>
      )}
      <div className={`flex-1 ${isCompact ? "flex justify-start" : "flex justify-center"}`}>
        <div
          className={
            isCompact
              ? `grid ${compactCols === 1 ? "grid-cols-1" : "grid-cols-2"} gap-1.5 w-full`
              : `inline-flex gap-1 p-1 rounded-xl border ${borders.default} ${surfaces.secondary}`
          }
        >
          {choices.map((opt) => {
            const selected = value === opt.value;
            return (
              <button
                type="button"
                key={`${opt.value}`}
                onClick={() => onChange(opt.value)}
                className={`px-3 py-1.5 text-center whitespace-nowrap focus:outline-none transition-all ${typography.button}
                  ${selected
                    ? `${interactive.selectedBg} text-white shadow-sm`
                    : `${surfaces.primary} ${textColors.primary} ${surfaces.hover}`
                  }
                  ${isCompact
                    ? `rounded-lg border ${selected ? interactive.selectedBorder : borders.default}`
                    : "rounded-lg"
                  }`}
                aria-pressed={selected}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
      {help && <div className={`mt-1 ${typography.caption} ${textColors.tertiary}`}>{help}</div>}
    </div>
  );
};
